import type { MessageContextResponse } from "@/lib/api/admin";
import type { RiskFlag } from "@/lib/api/risk-flagging";
import { CATEGORY_LABELS, CONTEXT_AFTER, CONTEXT_BEFORE, DRAFT_TTL } from "./constants";

export function getRecipient(alert: RiskFlag) {
  const { user, mentorship } = alert;
  return user?.id === mentorship?.mentor?.id
    ? mentorship?.mentee
    : mentorship?.mentor;
}

export function getCategoryLabel(alert: RiskFlag) {
  const cat = alert.categories?.[0];
  return cat ? (CATEGORY_LABELS[cat.category] ?? cat.category) : null;
}

export function trimContext(data: MessageContextResponse | undefined) {
  if (!data) return undefined;
  const idx = data.messages.findIndex((m) => m.isTarget);
  if (idx === -1) return data;
  const start = Math.max(0, idx - CONTEXT_BEFORE);
  const end = Math.min(data.messages.length, idx + CONTEXT_AFTER + 1);
  return { ...data, messages: data.messages.slice(start, end) };
}

export interface FeedbackDraft {
  feedbackCorrect: boolean;
  feedbackNote: string;
  expiresAt: number;
}

export function loadDraft(key: string): FeedbackDraft | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const draft: FeedbackDraft = JSON.parse(raw);
    if (draft.expiresAt && Date.now() > draft.expiresAt) {
      localStorage.removeItem(key);
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

export function isS3Url(url: string) {
  return url.includes("s3") || url.includes("bucket");
}

export function getMediaType(url: string): "image" | "video" | "audio" | null {
  const ext = url.split("?")[0].split(".").pop()?.toLowerCase();
  if (!ext) return null;
  if (["jpg", "jpeg", "png", "gif", "webp", "heic", "heif", "svg"].includes(ext)) return "image";
  if (["mp4", "mov", "webm", "avi", "mkv"].includes(ext)) return "video";
  if (["mp3", "wav", "m4a", "aac", "ogg", "opus"].includes(ext)) return "audio";
  return null;
}

export function saveDraft(key: string, correct: boolean | null, note: string) {
  if (correct === null) {
    localStorage.removeItem(key);
    return;
  }
  const draft: FeedbackDraft = {
    feedbackCorrect: correct,
    feedbackNote: note,
    expiresAt: Date.now() + DRAFT_TTL,
  };
  localStorage.setItem(key, JSON.stringify(draft));
}
